/**
 * Where a room's doors, windows and garage doors are in its 360, so the
 * panorama viewer can pin a callout on each (dimensions on hover).
 *
 * Frames: the designer's plan keeps openings in wall coordinates (feet along
 * the wall from its left end as seen from inside, feet up from the floor). The
 * exporter puts the room in a world frame centred on the room (x along the
 * length, y up, z along the width; Room3DViewer wallPointToWorld), and the
 * 360's camera in that same frame (the manifest's `panorama`; render-jobs.php
 * hands it back with the status). The equirectangular picture's centre column
 * is the camera's facing; PSV's yaw grows to the right of it.
 */

import type { FloorPlan, PlanRoom } from '../../../calculations/plan/floorPlan';
import type { OpeningKind } from '../../../types/sections';
import type { PanoramaCamera } from './types';

export type Vec3 = [number, number, number];
export type WallFace = 'front' | 'right' | 'back' | 'left';

/** `RoomSurfaces.walls` order. */
export const FACES: WallFace[] = ['front', 'right', 'back', 'left'];

/**
 * +1: a thing to the camera's right is right of the picture's centre. Settled
 * on a calibration render (2026-10-13, docs/360-VIEWER-DISCUSSION.md): facing
 * the garage-door wall, the wall to the camera's right came out on the right
 * half of Cycles' equirectangular picture, and PSV's yaw grows that way too.
 */
export const YAW_SIGN = 1;

/** A wall point (feet along the wall, feet up) in the room-centred world frame. */
export function wallPointToWorld(face: WallFace, sx: number, sy: number, length: number, width: number): Vec3 {
  switch (face) {
    case 'front': return [sx - length / 2, sy, -width / 2];
    case 'back': return [length / 2 - sx, sy, width / 2];
    case 'left': return [-length / 2, sy, sx - width / 2];
    case 'right': return [length / 2, sy, width / 2 - sx];
  }
}

/** The way the 360's camera faces (its target, else the wall it was said to open on). */
export function facingOf(pano: PanoramaCamera): Vec3 {
  if (pano.target) {
    const d: Vec3 = [pano.target[0] - pano.position[0], pano.target[1] - pano.position[1], pano.target[2] - pano.position[2]];
    const len = Math.hypot(d[0], d[1], d[2]);
    if (len > 1e-9) return [d[0] / len, d[1] / len, d[2] / len];
  }
  switch (pano.face) {
    case 'back': return [0, 0, 1];
    case 'left': return [-1, 0, 0];
    case 'right': return [1, 0, 0];
    default: return [0, 0, -1];
  }
}

/**
 * A world point as the viewer's yaw (radians, 0 straight ahead, positive to
 * the right) and pitch (radians, positive up) from the camera.
 */
export function toYawPitch(point: Vec3, camera: Vec3, facing: Vec3): { yaw: number; pitch: number } {
  const d: Vec3 = [point[0] - camera[0], point[1] - camera[1], point[2] - camera[2]];
  // Level forward and right (the 360 is rendered level).
  const fLen = Math.hypot(facing[0], facing[2]) || 1;
  const f: Vec3 = [facing[0] / fLen, 0, facing[2] / fLen];
  const r: Vec3 = [-f[2], 0, f[0]];
  const ahead = d[0] * f[0] + d[2] * f[2];
  const right = d[0] * r[0] + d[2] * r[2];
  return { yaw: YAW_SIGN * Math.atan2(right, ahead), pitch: Math.atan2(d[1], Math.hypot(d[0], d[2])) };
}

export interface OpeningMarker {
  id: string;
  kind: Exclude<OpeningKind, 'passage'>;
  wall: WallFace;
  yaw: number;
  pitch: number;
  /** Feet. */
  width: number;
  height: number;
}

/** The room the estimate is of: the focused one, else the first. */
export function roomOf(plan: FloorPlan, focusRoomId: string | null | undefined): PlanRoom | null {
  return plan.rooms.find((r) => r.id === focusRoomId) ?? plan.rooms[0] ?? null;
}

/** A callout per door, window and garage door of the room (passages between rooms have none). */
export function openingMarkers(plan: FloorPlan, focusRoomId: string | null | undefined, pano: PanoramaCamera): OpeningMarker[] {
  const room = roomOf(plan, focusRoomId);
  if (!room) return [];
  const facing = facingOf(pano);
  const out: OpeningMarker[] = [];
  room.surfaces.walls.forEach((wall, i) => {
    const face = FACES[i];
    if (!face) return;
    for (const o of wall.openings ?? []) {
      if (o.kind === 'passage') continue;
      const centre = wallPointToWorld(face, o.x + o.width / 2, o.y + o.height / 2, room.length, room.width);
      const { yaw, pitch } = toYawPitch(centre, pano.position, facing);
      out.push({ id: o.id, kind: o.kind, wall: face, yaw, pitch, width: o.width, height: o.height });
    }
  });
  return out;
}
